import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminGuard from "./components/AdminGuard";
import AdminLayout from "./components/AdminLayout";
import { useAdminSession } from "./components/AdminSessionContext";
import FilterBar, { EMPTY_FILTERS, type MemberFilters } from "./components/FilterBar";
import MemberTable from "./components/MemberTable";
import MemberDetailDrawer from "./components/MemberDetailDrawer";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { supabase } from "@/lib/supabase";
import type { MatchProfile } from "./adminConfig";

const PAGE_SIZE = 10;

function MembersContent() {
  const { isAdmin, isMatchmaker } = useAdminSession();
  const canManage = isAdmin || isMatchmaker;
  const canDelete = isAdmin;
  const [filters, setFilters] = useState<MemberFilters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<MemberFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [profiles, setProfiles] = useState<MatchProfile[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<MatchProfile | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MatchProfile | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      let query = supabase
        .from("match_profiles_masked")
        .select("*", { count: "exact" });

      if (applied.name.trim()) query = query.ilike("name", `%${applied.name.trim()}%`);
      if (canManage && applied.phone.trim()) query = query.ilike("phone", `%${applied.phone.trim()}%`);
      if (applied.gender) query = query.eq("gender", applied.gender);
      if (applied.city.trim()) query = query.ilike("residence", `%${applied.city.trim()}%`);
      if (applied.maritalStatus) query = query.eq("marital_status", applied.maritalStatus);
      if (applied.houseStatus) query = query.eq("house_status", applied.houseStatus);
      if (applied.childrenStatus) query = query.eq("children_status", applied.childrenStatus);
      const incomeKeyword = applied.incomeCustom.trim() || applied.income.trim();
      if (incomeKeyword) query = query.ilike("income", `%${incomeKeyword}%`);
      if (applied.ageMin) query = query.gte("age", Number(applied.ageMin));
      if (applied.ageMax) query = query.lte("age", Number(applied.ageMax));
      if (applied.heightMin) query = query.gte("height", Number(applied.heightMin));
      if (applied.heightMax) query = query.lte("height", Number(applied.heightMax));

      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      const { data, error: queryError, count } = await query
        .order("created_at", { ascending: false })
        .range(from, to);

      if (queryError) throw queryError;
      setProfiles((data as MatchProfile[]) || []);
      setTotal(count || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : "加载失败，请稍后重试");
      setProfiles([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [applied, page, canManage]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const handleFilterChange = (key: keyof MemberFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    setPage(1);
    setApplied({ ...filters });
  };

  const handleReset = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
    setApplied(EMPTY_FILTERS);
  };

  const handleUpdated = (updated: MatchProfile) => {
    setProfiles((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
    setSelected((prev) => (prev && prev.id === updated.id ? updated : prev));
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError("");
    try {
      const { error: deleteErr } = await supabase
        .from("match_profiles")
        .delete()
        .eq("id", deleteTarget.id);
      if (deleteErr) throw deleteErr;
      setProfiles((prev) => prev.filter((item) => item.id !== deleteTarget.id));
      setTotal((prev) => Math.max(0, prev - 1));
      setSelected((prev) => (prev && prev.id === deleteTarget.id ? null : prev));
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "删除失败，请稍后重试");
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            会员列表
          </h1>
          <p className="mt-1 text-sm text-foreground-500">共 {total} 条会员资料</p>
        </div>
        {canManage && (
          <div className="flex flex-col gap-2 sm:flex-row">
            <Link
              to="/admin/new"
              className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-user-add-line"></i>
              手动添加
            </Link>
            <Link
              to="/admin/import"
              className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-upload-cloud-2-line"></i>
              一键导入资料
            </Link>
          </div>
        )}
      </div>

      {!canManage && (
        <div className="flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3">
          <i className="ri-lock-2-line mt-0.5 text-secondary-600"></i>
          <p className="text-xs leading-relaxed text-secondary-900">
            当前为销售角色，为保护客户隐私，手机号已脱敏显示（如 138****5678）。如需完整号码请联系管理员。
          </p>
        </div>
      )}

      <FilterBar
        filters={filters}
        onChange={handleFilterChange}
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
        isAdmin={canManage}
      />

      <MemberTable
        rows={profiles}
        loading={loading}
        error={error}
        canDelete={canDelete}
        onRetry={loadProfiles}
        onSelect={setSelected}
        onDelete={setDeleteTarget}
      />

      {!loading && !error && totalPages > 1 && (
        <div className="flex flex-col items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-5 py-3.5 sm:flex-row">
          <p className="text-xs text-foreground-500">
            第 {page} / {totalPages} 页 · 共 {total} 条
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <i className="ri-arrow-left-s-line"></i>
              上一页
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              下一页
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </div>
      )}

      <MemberDetailDrawer
        profile={selected}
        canEdit={canManage}
        canDelete={canDelete}
        onClose={() => setSelected(null)}
        onUpdated={handleUpdated}
        onDelete={setDeleteTarget}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="删除会员资料"
        danger
        confirmLabel="确认删除"
        loading={deleting}
        message={`确定要删除「${deleteTarget?.name || "该会员"}」的资料吗？\n删除后不可恢复，请谨慎操作。${
          deleteError ? `\n\n删除失败：${deleteError}` : ""
        }`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteTarget(null);
          setDeleteError("");
        }}
      />
    </div>
  );
}

export default function AdminMembers() {
  return (
    <AdminGuard>
      <AdminLayout>
        <MembersContent />
      </AdminLayout>
    </AdminGuard>
  );
}